/**
 * The customer basket and checkout's refusals, bound to the inputs they name
 * (`.claude/rules/api-validation.md`) and the one-time price refusals the customer confirms.
 *
 * Headless, and no business wording: every sentence shown is the backend's. A refusal on something no
 * input holds (`lines.2.unavailable`) joins the banner in the server's own words.
 */

import { ApiError } from "../data/api/errors";
import { extractFieldErrors } from "../data/validation";

/** The inputs a basket or checkout refusal can name, in the page's own field names. */
const BASKET_FIELDS = [
  "payment_method",
  "customer_order_number",
  "required_on",
  "delivery_address_id",
  "delivery_lines",
  "quantity",
  "expected_price_pence",
  "speed_code",
  "check_id",
] as const;
export type CustomerBasketField = (typeof BASKET_FIELDS)[number];

/**
 * The Delivery section's inputs a Place order refusal can name (`deliveries.1.collection_point_id`), bound next to
 * that delivery's input. The index is the delivery's place in the `deliveries` the order sent.
 */
const DELIVERY_FIELDS = [
  "service_level",
  "collect",
  "collection_point_id",
  "recipient_name",
  "recipient_phone",
  "recipient_email",
] as const;
export type CustomerDeliveryField = (typeof DELIVERY_FIELDS)[number];

export interface CustomerDeliveryFieldError {
  /** Index into the `deliveries` the order sent. */
  index: number;
  field: CustomerDeliveryField;
  message: string;
}

/** A refusal's delivery errors by the delivery they name, given the refs in the order they were sent. */
export function deliveryErrorsByRef(
  errors: ReadonlyArray<CustomerDeliveryFieldError>,
  sentRefs: readonly string[],
): Record<string, Partial<Record<CustomerDeliveryField, string>>> {
  const byRef: Record<
    string,
    Partial<Record<CustomerDeliveryField, string>>
  > = {};
  for (const e of errors) {
    const ref = sentRefs[e.index];
    if (ref === undefined) continue;
    byRef[ref] = { ...byRef[ref], [e.field]: e.message };
  }
  return byRef;
}

/** A line whose price moved between the basket and the checkout, ex VAT. */
export interface CustomerChangedPrice {
  /** 1-based, as the basket lists its lines. */
  position: number;
  oldPricePence: number;
  newPricePence: number;
}

/** A delivery whose price is not the one the page showed (null: Not yet priced), ex VAT, in the server's words. */
export interface CustomerChangedDelivery {
  deliveryRef: string;
  oldPricePence: number | null;
  newPricePence: number | null;
  /** The server's sentence for it: where it goes, what it was and what it is now. */
  message: string;
}

export interface CustomerBasketFailure {
  /** The banner: the server's words for what no input holds, and a count when inputs are highlighted. */
  summary: string;
  /** The backend's message for each input it refused, to show beside that input. */
  fieldErrors: Partial<Record<CustomerBasketField, string>>;
  /** The backend's message for each Delivery section input it refused. */
  deliveryErrors: CustomerDeliveryFieldError[];
  /** Set when the checkout stopped because prices changed: the basket now holds the new prices. */
  changedPrices: CustomerChangedPrice[] | null;
  /** Set when the checkout stopped because a delivery's price is no longer the one shown: the section reads again. */
  changedDeliveries: CustomerChangedDelivery[] | null;
  /** Set when an add or quantity change was refused once because the price had moved. */
  movedPrice: { newPricePence: number } | null;
}

function fieldOf(field: string): CustomerBasketField | null {
  const input = field.split(".")[0] ?? field;
  return (BASKET_FIELDS as readonly string[]).includes(input)
    ? (input as CustomerBasketField)
    : null;
}

/** `deliveries.1.collection_point_id` → the second delivery's point; null for any other field. */
function deliveryInputOf(
  field: string,
): { index: number; field: CustomerDeliveryField } | null {
  const match = /^deliveries\.(\d+)\.(\w+)$/.exec(field);
  if (
    !match ||
    !(DELIVERY_FIELDS as readonly string[]).includes(match[2] as string)
  )
    return null;
  return { index: Number(match[1]), field: match[2] as CustomerDeliveryField };
}

function detailOf(err: unknown): Record<string, unknown> | null {
  if (!(err instanceof ApiError)) return null;
  const detail = (err.body as { detail?: unknown } | null)?.detail;
  return detail && typeof detail === "object"
    ? (detail as Record<string, unknown>)
    : null;
}

function changedPricesOf(err: unknown): CustomerChangedPrice[] | null {
  const detail = detailOf(err);
  if (
    !(err instanceof ApiError) ||
    err.status !== 409 ||
    detail?.reason_code !== "prices_changed" ||
    !Array.isArray(detail.changes)
  )
    return null;
  return (
    detail.changes as Array<{
      position: number;
      old_price_pence: number;
      new_price_pence: number;
    }>
  ).map((c) => ({
    position: c.position,
    oldPricePence: c.old_price_pence,
    newPricePence: c.new_price_pence,
  }));
}

function changedDeliveriesOf(err: unknown): CustomerChangedDelivery[] | null {
  const detail = detailOf(err);
  if (
    !(err instanceof ApiError) ||
    err.status !== 409 ||
    detail?.reason_code !== "prices_changed" ||
    !Array.isArray(detail.delivery_changes)
  )
    return null;
  return (
    detail.delivery_changes as Array<{
      delivery_ref: string;
      old_price_pence: number | null;
      new_price_pence: number | null;
      message: string;
    }>
  ).map((c) => ({
    deliveryRef: c.delivery_ref,
    oldPricePence: c.old_price_pence,
    newPricePence: c.new_price_pence,
    message: c.message,
  }));
}

function movedPriceOf(err: unknown): { newPricePence: number } | null {
  const detail = detailOf(err);
  if (
    !(err instanceof ApiError) ||
    err.status !== 409 ||
    detail?.reason_code !== "price_moved" ||
    typeof detail.new_price_pence !== "number"
  )
    return null;
  return { newPricePence: detail.new_price_pence };
}

function serverMessage(err: unknown): string {
  const message = detailOf(err)?.message;
  if (typeof message === "string") return message;
  if (err instanceof ApiError && err.detail) return err.detail;
  return err instanceof Error
    ? err.message
    : "We couldn't do that just now. Please try again.";
}

/**
 * What to show when a basket step or checkout is refused. A refusal on one of the page's inputs is bound
 * to it and counted in the banner; anything else (an unavailable line, a basket already checked out,
 * prices that changed) is the server's own message, verbatim.
 */
export function customerBasketFailure(err: unknown): CustomerBasketFailure {
  const fieldErrors: CustomerBasketFailure["fieldErrors"] = {};
  const deliveryErrors: CustomerDeliveryFieldError[] = [];
  const unbound: string[] = [];
  for (const e of extractFieldErrors(err)) {
    const input = fieldOf(e.field);
    const deliveryInput = deliveryInputOf(e.field);
    if (input) fieldErrors[input] ??= e.message;
    else if (deliveryInput)
      deliveryErrors.push({ ...deliveryInput, message: e.message });
    else unbound.push(e.message);
  }
  const changedPrices = changedPricesOf(err);
  const changedDeliveries = changedDeliveriesOf(err);
  const movedPrice = movedPriceOf(err);
  const count = Object.keys(fieldErrors).length + deliveryErrors.length;
  const counted =
    count === 0
      ? null
      : count === 1
        ? "One field needs correcting."
        : `${count} fields need correcting.`;
  if (counted || unbound.length > 0) {
    return {
      summary: [...unbound, counted].filter(Boolean).join(" "),
      fieldErrors,
      deliveryErrors,
      changedPrices,
      changedDeliveries,
      movedPrice,
    };
  }
  return {
    summary: serverMessage(err),
    fieldErrors,
    deliveryErrors,
    changedPrices,
    changedDeliveries,
    movedPrice,
  };
}
